/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { SortOption } from '@/lib/search/sort-options';
import { useSortSelection } from './use-sort-selection';

/**
 * NM's MOBILE sort control: a text trigger in the sticky bar that opens live
 * NM's own left-anchored `SORT BY:` panel.
 *
 * WHAT THIS REPLACES, AND WHY IT COULD NOT BE BUILT BEFORE. Until 2026-08-23
 * the trigger had a transparent native `<select>` stretched over it, and
 * `sticky-bar.tsx` recorded the reason in full: live's button opens the site's
 * own panel, and THAT PANEL HAD NEVER BEEN CAPTURED at any mobile width, so its
 * width, ground, row rhythm and selected state were all unmeasured. A native
 * select's popup is drawn by the OS, so no rule could reach it either — which
 * is what the owner saw and reported as *"our sort popup is a crazy tiny
 * desktop thing"*. The capture now exists and the deferral is retired.
 *
 * THE EVIDENCE, and how each number was obtained. The only way to see live NM
 * mobile is a photograph of a real handset — live NM serves a desktop response
 * to anything else — so the source is
 * `.reference/owner-nm-mobile-2026-08-23/5f8b5b92-…jpeg`, iOS Safari, address
 * bar reading `neimanmarcus.com`. Sampled through a colour-managed canvas:
 *
 *   image 942 x 2046; the page viewport is the full image width and runs
 *   y 241..1878 between Safari's own chrome.
 *
 *   panel        leading white run 835/942 = 88.64% of the viewport width,
 *                held for 1366 consecutive scan rows, spanning y 241..1878 —
 *                i.e. the FULL viewport height. The page shows through the
 *                remaining 11.4%, dimmed: the scrim samples rgb(73,73,73)
 *                over what is white page beneath.
 *   header ink   y 268..300  ->  11.2 .. 24.4 CSS from the panel's top edge
 *   options      ink bands at y 398, 501, 604, 706, 809 — five of them, which
 *                is NM's five-row list — first ink at 65.0 CSS from the top
 *                and a pitch of 103, 103, 102, 103 px = 42.6 CSS.
 *
 * THE PANEL WIDTH IS A PERCENTAGE ON PURPOSE. 88.64% and `calc(100% - 44px)`
 * are indistinguishable from a single capture (they differ by 0.3px at 390),
 * and a percentage is what was actually measured. It is also the formulation
 * that degrades sanely at the other mobile widths, where nothing was measured.
 *
 * THE CSS-PIXEL VALUES CARRY ±1%, and the reason is stated rather than hidden:
 * a photograph does not record its own device, so the scale factor is derived
 * from an ASSUMED 390 CSS viewport (942/390 = 2.415 px/CSS). At 393 — an iPhone
 * 14/15 — the pitch reads 43.0 rather than 42.6. The RATIOS above (panel width,
 * full height) carry no such assumption and are exact.
 *
 * WHY THE LABELS ARE `capitalize` RATHER THAN NEW COPY. The photograph shows
 * `Price: High To Low`; live NM's own hydration state, transcribed on
 * `NM_SORT_OPTIONS`, carries `Price: high to low`. Same string, a CSS transform
 * on the mobile panel — which is also why `Newest First`, `Best Sellers` and
 * `Best Match` read identically in both. Applying it as a transform rather than
 * forking the copy means BM-provided server labels get the same treatment, so
 * the panel cannot drift from the list the loader actually sorted by.
 *
 * NM ONLY BY MOUNT, which is why nothing here is brand-keyed — the same
 * arrangement `sticky-bar.tsx` documents. This renders inside `SearchStickyBar`,
 * which the routes mount behind `SearchUx.mobileStickyBar` (true for NM, false
 * for BG), and the bar itself is `md:hidden`. BG's mobile sort is a DIFFERENT
 * CONTROL with a different shape — full-screen, centred title, close at the
 * leading edge, underline instead of radios — and lives in
 * `bg-mobile-sort-overlay.tsx`. The two are not one component with a flag: they
 * share no band, and the findings this was built from say so outright.
 *
 * ACCESSIBILITY COMES FROM `Sheet`, NOT FROM HAND-ROLLED CODE. `Sheet` is this
 * repo's Radix dialog wrapper, already carrying the focus trap, Escape-to-close,
 * focus restoration to the trigger, the scroll lock, and `aria-hidden` on
 * everything behind the portal.
 *
 * IT DOES NOT SET `aria-modal`, and that is checked rather than assumed —
 * dumping the rendered attributes gives `role="dialog"`, `aria-labelledby` and
 * `data-state`, and no `aria-modal`. Radix hides the background instead: every
 * body child except the portal gains `aria-hidden="true"`. That is the STRONGER
 * of the two mechanisms — `aria-modal` is advisory and has patchy support,
 * whereas hiding the siblings removes them from the accessibility tree outright
 * — so the guarantee is real, but a test asserting `aria-modal` would redden
 * against a correct implementation. Assert the hidden siblings. `bg-search-panel.tsx` — BG's
 * full-screen header search takeover — is the established precedent for using
 * it this way, including the two escape hatches this surface also needs:
 * `closeButton={null}` (live's close is not where the stock one is) and
 * `overlayClassName` (the scrim is a portal SIBLING, unreachable from a
 * descendant variant on `className`).
 *
 * A `RadioGroup` IS NOT USED even though live paints radios. Radix's radio
 * group is a form control that owns arrow-key roving focus and reports a value;
 * these rows navigate on activation, so a menu of `menuitemradio` buttons is
 * the honest role — the same one `sort-menu.tsx` uses for BG's desktop panel.
 * The radio MARK is drawn, because that is what live paints.
 *
 * FOCUS IS RESTORED EXPLICITLY, NOT LEFT TO RADIX. Radix returns focus to
 * whatever held it before the pane opened — which is nothing here. On macOS and
 * on touch, clicking a `<button>` does not focus it, so at the moment of
 * opening `document.activeElement` is the body and there is nothing to restore
 * TO. Measured on the running app under iPhone 12 Pro emulation before this
 * was added: after Escape, `document.activeElement` was the body and the
 * shopper's place in the page was gone. `onCloseAutoFocus` is therefore
 * intercepted and the trigger focused by hand, which is correct on every
 * platform rather than only on the ones where a click happens to focus.
 *
 * NOT `React.lazy`. Rule 18 wants overlays deferred, and this one already is in
 * the sense that matters: `Sheet` portals nothing while closed, so the at-rest
 * DOM is a button. Wrapping the remainder in a Suspense boundary inside a
 * STICKY BAR would put an unreserved async boundary in a fixed-height strip,
 * which is the CLS shape rules 8 and 18 are both trying to avoid, in exchange
 * for a component whose whole payload is this file.
 */
export default function NmMobileSortOverlay({
    serverOptions,
    fallbackOptions,
}: {
    serverOptions?: Array<{ id?: string; label?: string }>;
    /** The brand's curated list — named by the caller, never defaulted. */
    fallbackOptions: readonly SortOption[];
}): ReactElement {
    const { t } = useTranslation('search');
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const { options, selected, labelOf, choose, isPending } = useSortSelection(serverOptions, fallbackOptions);

    // Live's trigger reads "Sort By" with no value beside it — see the
    // `StickySortTrigger` docblock, which measured that against live's own
    // capture. The panel's title carries the colon; the trigger does not.
    const sortByLabel = t('sort.labelBare', { defaultValue: 'Sort By' });
    const panelTitle = t('sort.label', { defaultValue: 'Sort By:' });

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                data-testid="search-sticky-sort-trigger"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen(true)}
                className={cn(
                    'focus-ring-button relative inline-flex cursor-pointer items-baseline',
                    isPending && 'pointer-events-none opacity-50 transition-opacity'
                )}>
                <span className="h-(--srp-sticky-sort-height) ps-(--srp-sticky-sort-pad-start) pe-(--srp-sticky-sort-pad-end) text-(length:--srp-sticky-sort-font-size) leading-(--srp-sticky-sort-line-height) font-bold tracking-(--srp-sticky-sort-letter-spacing) whitespace-nowrap">
                    {sortByLabel}
                </span>
                <SortChevron />
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="left"
                    data-testid="nm-mobile-sort-overlay"
                    /* BOTH max-width classes are load-bearing, and the responsive
                       one is the whole point. `SheetContent`'s `side="left"` arm
                       ends in `sm:max-w-sm`, which caps the panel at 384px from
                       640 up — inside the mobile tier this overlay serves, since
                       `md` is 768. So between 640 and 767 the measured 88.64%
                       would silently become a 384px column.

                       `max-w-none` ALONE DOES NOT FIX IT, which was verified
                       rather than assumed: tailwind-merge treats a base utility
                       and its `sm:` variant as different properties, so both
                       survive into the class list and the variant still wins
                       above 640. Dumping the rendered attributes showed
                       `sm:max-w-sm` sitting next to `max-w-none` on the element.
                       The `sm:` arm has to be cancelled by its own `sm:` arm. */
                    className="srp-mobile-sort-panel w-(--srp-mobile-sort-panel-width) max-w-none sm:max-w-none gap-0 border-0 p-0 [--ui-shadow:none]"
                    overlayClassName="srp-mobile-sort-scrim"
                    /* Radix auto-wires `aria-describedby` at an id it then
                       expects a `Dialog.Description` to claim. This pane has no
                       description — its title and its five rows are the whole
                       content — so the opt-out is explicit. Left implicit it
                       ships a dangling IDREF, which is an a11y defect that
                       resolves to nothing and which Radix warns about at
                       runtime. */
                    aria-describedby={undefined}
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        triggerRef.current?.focus();
                    }}
                    closeButton={null}>
                    <div className="srp-mobile-sort-header flex items-center justify-between">
                        <SheetTitle className="srp-mobile-sort-title">{panelTitle}</SheetTitle>
                        <button
                            type="button"
                            data-testid="nm-mobile-sort-close"
                            onClick={() => setOpen(false)}
                            aria-label={t('sort.closePanel', { defaultValue: 'Close sort options' })}
                            className="focus-ring-button srp-mobile-sort-close cursor-pointer">
                            <CloseGlyph />
                        </button>
                    </div>

                    <ul
                        role="menu"
                        aria-label={panelTitle}
                        data-testid="nm-mobile-sort-list"
                        className="srp-mobile-sort-list overflow-y-auto">
                        {options.map((option) => {
                            const isSelected = option.id === selected?.id;
                            return (
                                <li key={option.id} role="none">
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={isSelected}
                                        data-testid="nm-mobile-sort-option"
                                        data-selected={isSelected ? '' : undefined}
                                        onClick={() => {
                                            choose(option.id);
                                            setOpen(false);
                                        }}
                                        className="srp-mobile-sort-option focus-ring-button flex w-full cursor-pointer items-center text-left">
                                        <RadioMark checked={isSelected} />
                                        <span className="srp-mobile-sort-option-label">{labelOf(option)}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </SheetContent>
            </Sheet>
        </>
    );
}

/**
 * The 12x8 caret beside the trigger — unchanged from the control this replaced.
 * Live's own glyph is an `<img alt="sort down icon">` whose src is an EXTERNAL
 * storage URL in the capture rather than a data URI, so nothing about its
 * geometry is recoverable beyond the 12x8 box its class declares. The BOX is
 * live's; the stroke is the storefront's own caret.
 */
function SortChevron(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 10 6"
            fill="none"
            className="w-(--srp-sticky-sort-chevron-width) h-(--srp-sticky-sort-chevron-height) shrink-0">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}

/**
 * The panel's dismiss mark. Live paints a heavy X at the panel's TRAILING edge
 * — which is the half of this that is measured, and it is the opposite side
 * from BG, whose X sits at the leading edge. The PATH is the storefront's own
 * two-stroke cross rather than live's: the photograph resolves its position and
 * its weight relative to the type beside it, and not its geometry.
 */
function CloseGlyph(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            fill="none"
            className="size-(--srp-mobile-sort-close-size)">
            <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" />
        </svg>
    );
}

/**
 * Live's radio mark: an outlined circle that gains a filled centre when the row
 * is the selected sort. Decorative — `aria-checked` on the row's own
 * `menuitemradio` carries the state, so a screen reader never meets this.
 *
 * NOTHING MOVES BETWEEN THE TWO STATES. The ring is drawn at rest and the dot
 * is added inside it, rather than the ring thickening — the same no-reflow
 * discipline BG's underline uses on the desktop panel, and for the same reason:
 * a mark that changes its own box shifts every row below it by a pixel.
 */
function RadioMark({ checked }: { checked: boolean }): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            fill="none"
            className="srp-mobile-sort-radio shrink-0">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
            {checked && <circle cx="12" cy="12" r="5.5" fill="currentColor" />}
        </svg>
    );
}
